import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { PageHero, SectionHead } from "@/components/site/PageHero";
import { Reveal } from "@/components/site/Reveal";
import { BrandStructure } from "@/components/site/BrandStructure";
import { Marquee } from "@/components/site/Marquee";
import { COMPANY } from "@/config/site";
import { usePageMeta } from "@/hooks/usePageMeta";

const principles = [
  { t: "Focused execution", d: "Each brand is built with a clear category focus and its own audience in mind." },
  { t: "Accessible offerings", d: "We aim to make useful products and commerce solutions easier to reach." },
  { t: "Meaningful partnerships", d: "We look to grow alongside manufacturers, distributors, retailers and suppliers." },
  { t: "Distinct brand identities", d: "Every brand keeps its own identity, voice and business model." },
];

export default function About() {
  usePageMeta("About Us", "Learn about Suvirika Retail Pvt Ltd, a multi-brand Indian retail and consumer products company with eight distinct brands.");
  return (
    <>
      <PageHero
        eyebrow="About Us"
        lines={["A parent company", <em key="i" className="italic font-light text-sky-ink">for eight distinct</em>, "consumer brands."]}
        intro={COMPANY.intro}
        testId="about-hero"
      />
      <section className="pb-24 md:pb-32">
        <div className="container-x grid gap-12 lg:grid-cols-12 lg:items-center">
          <Reveal className="lg:col-span-5">
            <div className="flex aspect-square items-center justify-center border hairline bg-white p-10">
              <img src={COMPANY.logo} alt={`${COMPANY.name} logo`} width="420" height="330" className="w-full max-w-sm" data-testid="about-logo" />
            </div>
          </Reveal>
          <div className="lg:col-span-7 lg:pl-10">
            <Reveal><p className="eyebrow">Brand Architecture</p></Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-display mt-5 text-3xl leading-tight sm:text-4xl">One parent company. Eight brands. Each with its own audience, identity and business model.</h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-6 leading-relaxed text-[var(--ink-2)] md:text-lg">
                Suvirika Retail Pvt Ltd brings together businesses across B2B distribution, local commerce, beauty and personal care, FMCG, accessories, intimate apparel and home care. The parent company provides a shared direction, while each brand is developed for the customers and partners it serves.
              </p>
            </Reveal>
          </div>
        </div>
      </section>
      <Marquee />
      <section className="py-24 md:py-32">
        <div className="container-x">
          <SectionHead eyebrow="Structure" lines={["How the group", <em key="i" className="italic font-light text-rose-ink">is organised.</em>]} />
          <div className="mt-14 max-w-4xl"><BrandStructure /></div>
        </div>
      </section>
      <section className="bg-[var(--paper-2)] py-24 md:py-32">
        <div className="container-x">
          <SectionHead eyebrow="What guides us" lines={["Principles we", <em key="i" className="italic font-light text-sky-ink">build on.</em>]} />
          <div className="mt-14 grid gap-px border hairline bg-[var(--line)] sm:grid-cols-2">
            {principles.map((p, i) => (
              <Reveal key={p.t} delay={i * 0.08} className="bg-[var(--paper-2)] p-8 md:p-12" data-testid={`principle-${i}`}>
                <span className="font-mono-label text-xs text-[var(--ink-2)]">0{i + 1}</span>
                <h3 className="font-display mt-6 text-3xl">{p.t}</h3>
                <p className="mt-3 max-w-md leading-relaxed text-[var(--ink-2)]">{p.d}</p>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-12 flex flex-wrap gap-4">
            <Link to="/brands" className="btn-ink" data-testid="about-brands-btn">Explore Our Brands <ArrowUpRight size={18} /></Link>
            <Link to="/careers" className="btn-line" data-testid="about-careers-btn">Careers</Link>
          </Reveal>
        </div>
      </section>
    </>
  );
}
